import { Link } from "react-router-dom";
import { useSEO } from "../hooks/useSEO";

const EditorialPage = () => {
  useSEO({
    title: "Editorial Process & Sources — TaxShift 2026",
    description:
      "How TaxShift 2026 researches, drafts, fact-checks, and updates its OBBBA tax coverage. Sources, update cadence, corrections policy, and conflict-of-interest statement.",
    path: "/editorial",
  });

  return (
    <div className="py-16 md:py-24" data-testid="editorial-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-4 block">
          Editorial Standards
        </span>
        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-8"
          data-testid="editorial-title"
        >
          How we cover OBBBA.
        </h1>
        <p className="font-body text-lg leading-relaxed text-foreground/85 mb-10">
          TaxShift 2026 is an independent editorial publication covering federal
          and state tax law in the United States after the One Big Beautiful Bill
          Act (Public Law 119-21, signed July 4, 2025). This page documents how
          we research, write, fact-check, and update everything you read on the
          site — so you can judge our work for yourself.
        </p>

        <div className="space-y-10 font-body text-base leading-relaxed text-foreground/85">
          <section data-testid="editorial-sources">
            <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
              Primary sources
            </h2>
            <p className="mb-4">
              Every dollar figure, threshold, phase-out, and effective date on
              this site is taken from primary, government-published material.
              The five sources we rely on, in order of precedence:
            </p>
            <ol className="list-decimal pl-6 space-y-2.5">
              <li>
                <strong>Public Law 119-21 (OBBBA)</strong> — the enacted
                statutory text signed July 4, 2025. The bill is the controlling
                authority for every provision we cover.
              </li>
              <li>
                <strong>IRS Revenue Procedures</strong> — Rev. Proc. 2024-40
                (2025 inflation adjustments) and Rev. Proc. 2025-32 (2026
                inflation adjustments) provide the bracket figures, standard
                deduction amounts, and credit thresholds used in every
                calculator.
              </li>
              <li>
                <strong>IRS Notices and Treasury Regulations</strong> — including
                Notice 2025-87 (qualified tipped-occupation list) and any
                subsequent guidance the IRS releases on OBBBA provisions.
              </li>
              <li>
                <strong>Joint Committee on Taxation (JCT) explanations</strong> —
                the JCT's General Explanation of OBBBA (the "Blue Book") and
                interim JCX documents provide the legislative-intent context
                for ambiguous provisions.
              </li>
              <li>
                <strong>State Departments of Revenue</strong> — for each of the
                51 state-level calculator pages, 2026 bracket and rate data
                comes from the official DOR or equivalent agency in that
                jurisdiction.
              </li>
            </ol>
            <p className="mt-4">
              We do not rely on press summaries, lobbying group analyses, or
              third-party tax-software companies for statutory figures. Where a
              calculation uses an estimate (for example, a phase-out slope still
              awaiting final Treasury regulations), we mark the assumption in
              the calculator's "Understanding your results" panel.
            </p>
          </section>

          <section data-testid="editorial-process">
            <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
              Editorial process
            </h2>
            <p className="mb-3">
              Every article and calculator on the site goes through the
              following six-stage process before publishing:
            </p>
            <ol className="list-decimal pl-6 space-y-2.5">
              <li>
                <strong>Statutory read.</strong> The drafter reads the relevant
                section of P.L. 119-21 in full, plus any IRS guidance issued.
              </li>
              <li>
                <strong>Bracket and threshold verification.</strong> Every
                numeric value is cross-checked against IRS Revenue Procedure
                tables. The same value must appear in two independent primary
                sources to ship.
              </li>
              <li>
                <strong>Worked-example drafting.</strong> At least one realistic
                household scenario with explicit dollar amounts is built for
                each article. These are the most-cited test of whether the
                explanation is actually usable.
              </li>
              <li>
                <strong>Calculator implementation.</strong> Where applicable, a
                Python/JavaScript calculator implements the underlying math.
                The calculator must reproduce the worked example to the dollar.
              </li>
              <li>
                <strong>Conformity check.</strong> Articles touching deductions
                or credits that vary by state include a state-conformity note,
                referencing the relevant DOR position.
              </li>
              <li>
                <strong>Disclosure pass.</strong> Each article is reviewed for
                completeness of disclosures, including the explicit reminder
                that we publish educational analysis, not personalized tax
                advice.
              </li>
            </ol>
          </section>

          <section data-testid="editorial-cadence">
            <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
              Update cadence
            </h2>
            <p className="mb-3">
              Tax law is a moving target. Our maintenance schedule:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Annually (October–November).</strong> When the IRS
                releases the next year's Revenue Procedure with updated
                inflation adjustments, every calculator and bracket-related
                article is re-verified and updated.
              </li>
              <li>
                <strong>On Treasury guidance.</strong> When the IRS publishes
                new notices, regulations, or revenue rulings on OBBBA
                provisions, the affected articles are updated within two weeks.
              </li>
              <li>
                <strong>On statutory change.</strong> If Congress enacts a
                modification to OBBBA (extension, repeal, or amendment), the
                affected coverage is rewritten and the change is noted at the
                top of the article.
              </li>
              <li>
                <strong>Quarterly link sweep.</strong> Internal links, footnote
                citations, and external references are checked every quarter.
              </li>
            </ul>
          </section>

          <section data-testid="editorial-corrections">
            <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
              Corrections policy
            </h2>
            <p>
              If you find a factual error — a misquoted threshold, an outdated
              phase-out, an incorrect worked example — please write to us via
              the <Link to="/contact" className="underline text-primary">contact form</Link>.
              We aim to acknowledge corrections within 48 hours and publish
              substantive corrections at the top of the affected article. Math
              errors in calculators are treated as P0 issues and patched within
              one business day.
            </p>
          </section>

          <section data-testid="editorial-independence">
            <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
              Independence & conflicts of interest
            </h2>
            <p className="mb-3">
              TaxShift 2026 is independently owned and operated. It is funded
              entirely by display advertising served through Google AdSense.
              Specifically:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                We do not accept paid placements, sponsored articles, or
                affiliate-fee content from tax-preparation firms, tax-advisory
                firms, financial-planning firms, law firms, or accounting
                firms.
              </li>
              <li>
                We do not sell email addresses collected through the
                newsletter. The subscriber list is used only to send our own
                editorial briefings.
              </li>
              <li>
                Calculator results are not personalized, are not stored on our
                servers, and are computed entirely in your browser. No data is
                transmitted to us when you use a calculator.
              </li>
              <li>
                Display ads on the site are served by Google AdSense based on
                Google's targeting. We do not select advertisers and do not
                endorse the products advertised.
              </li>
            </ul>
          </section>

          <section data-testid="editorial-team">
            <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
              About the editorial team
            </h2>
            <p>
              TaxShift 2026 is researched and written by a small editorial
              desk focused on US federal and state tax policy. The team reads
              the statute, follows IRS guidance as it is issued, and turns the
              technical material into plain-English coverage with worked
              examples. We are not licensed CPAs, Enrolled Agents, or tax
              attorneys, and nothing on this site constitutes a tax opinion
              under Circular 230. For decisions affecting your finances, work
              with a credentialed practitioner. Our role is to help you
              understand the law well enough to ask better questions.
            </p>
          </section>

          <section data-testid="editorial-scope">
            <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
              What we do not cover
            </h2>
            <p>
              We focus narrowly on the post-OBBBA federal individual,
              small-business, and estate tax landscape, plus state-level
              income tax for the 41 jurisdictions that levy one. We do not
              cover: corporate tax (C-corp returns), international tax
              compliance (FBAR, GILTI, FDII), payroll-tax compliance for
              employers, sales-and-use tax beyond the SALT deduction
              discussion, property assessment disputes, or any non-US tax
              regime. If you need coverage outside this scope, a credentialed
              tax professional will serve you better than any web publication.
            </p>
          </section>

          <section data-testid="editorial-contact">
            <h2 className="font-heading text-3xl font-semibold tracking-tight mb-4">
              Contact
            </h2>
            <p>
              Editorial questions, source corrections, and reader feedback can
              be sent through the{" "}
              <Link to="/contact" className="underline text-primary">
                contact page
              </Link>
              . For the formal disclaimer and limits of our work, see the{" "}
              <Link to="/disclaimer" className="underline text-primary">
                disclaimer
              </Link>
              . For how we handle data, see our{" "}
              <Link to="/privacy-policy" className="underline text-primary">
                privacy policy
              </Link>{" "}
              and{" "}
              <Link to="/gdpr" className="underline text-primary">
                GDPR notice
              </Link>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default EditorialPage;
